import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import UndoIcon from '@mui/icons-material/Undo';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import NumericInput from '@/components/NumericInput';
import ConfirmDialog from '@/components/modals/ConfirmDialog';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { countStock, getBranchStocks } from '@/data/stockService';
import { BranchStock, StockUnit } from '@/types/stock';
import { matchesQuery } from '@/utils/textSearch';
import { prepareStockUnitLabel } from '@/utils/helpers';
import {
  applyDrift,
  buildCountItems,
  countDifference,
  CountedMap,
  hasDifference,
  loadCountDraft,
  saveCountDraft
} from '@/utils/stockCount';

const PAGE = 500;

const formatQuantity = (value: number) =>
  value.toLocaleString('tr-TR', { maximumFractionDigits: 3 });

/**
 * Stok sayımı: every stock row of the branch on one screen; the counter
 * types what is on the shelf, then all differences are saved at once.
 */
function StockCountPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentBranch, isAdminView } = useUserViewMode();
  const branchId = !isAdminView ? (currentBranch?.id ?? null) : null;

  const [rows, setRows] = useState<BranchStock[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [counted, setCounted] = useState<CountedMap>({});
  // System quantity of each counted row when it was counted (sent as the
  // expected quantity, so stock that moved since comes back as drift).
  const [expected, setExpected] = useState<CountedMap>({});
  const [search, setSearch] = useState('');
  const [warehouseId, setWarehouseId] = useState<number | ''>('');
  const [onlyCounted, setOnlyCounted] = useState(false);
  const [note, setNote] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drifted, setDrifted] = useState<Set<number>>(new Set());
  const [message, setMessage] = useState<{
    severity: 'success' | 'warning' | 'error';
    text: string;
  } | null>(null);

  // A branch switch while loading must not show the previous branch's rows.
  const loadGeneration = useRef(0);

  const loadRows = useCallback(async () => {
    if (!branchId) return;
    const generation = ++loadGeneration.current;
    setLoading(true);
    setLoadError(false);
    try {
      const all: BranchStock[] = [];
      for (let page = 0; ; page += 1) {
        const result = await getBranchStocks({ branchId, page, limit: PAGE });
        if (generation !== loadGeneration.current) return;
        all.push(...result.items);
        if (all.length >= result.total || result.items.length < PAGE) break;
      }
      setRows(
        all
          .filter((row) => row.batch?.status !== 'DISPOSED')
          .sort((a, b) =>
            a.companyProduct.name.localeCompare(b.companyProduct.name, 'tr')
          )
      );
    } catch (error) {
      if (generation !== loadGeneration.current) return;
      console.error('Stock count rows failed:', error);
      setLoadError(true);
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    if (!branchId) return;
    const draft = loadCountDraft(branchId);
    setCounted(draft.counted);
    setExpected(draft.expected);
    setDrifted(new Set());
    setRows([]);
    loadRows();
  }, [branchId, loadRows]);

  const saveDraft = (nextCounted: CountedMap, nextExpected: CountedMap) => {
    setCounted(nextCounted);
    setExpected(nextExpected);
    if (branchId) {
      saveCountDraft(branchId, {
        counted: nextCounted,
        expected: nextExpected
      });
    }
  };

  const setRowCount = (row: BranchStock, value: number) =>
    saveDraft(
      { ...counted, [row.id]: value },
      // The first quantity seen while counting this row is what was counted
      // against (a reload must not move it).
      { ...expected, [row.id]: expected[row.id] ?? row.quantity }
    );

  const clearRowCount = (rowId: number) => {
    if (counted[rowId] === undefined && expected[rowId] === undefined) return;
    const nextCounted = { ...counted };
    const nextExpected = { ...expected };
    delete nextCounted[rowId];
    delete nextExpected[rowId];
    saveDraft(nextCounted, nextExpected);
  };

  const warehouses = useMemo(() => {
    const byId = new Map<number, string>();
    rows.forEach((row) => byId.set(row.warehouse.id, row.warehouse.name));
    return [...byId.entries()];
  }, [rows]);

  const visible = rows.filter(
    (row) =>
      (warehouseId === '' || row.warehouse.id === warehouseId) &&
      (!onlyCounted || counted[row.id] !== undefined) &&
      matchesQuery(
        `${row.companyProduct.name} ${row.batch?.batchNumber ?? ''}`,
        search
      )
  );
  const items = buildCountItems(rows, counted, expected);
  const changedCount = rows.filter((row) => hasDifference(row, counted)).length;

  const handleSave = async () => {
    if (!branchId) return;
    setConfirmOpen(false);
    setSaving(true);
    try {
      const result = await countStock({
        branchId,
        note: note.trim() || undefined,
        items
      });
      saveDraft({}, {});
      setDrifted(new Set());
      setNote('');
      setMessage({
        severity: 'success',
        text: t('stockCount.saved', {
          adjusted: result.adjusted,
          unchanged: result.unchanged
        })
      });
      await loadRows();
    } catch (error) {
      const data = (
        error as {
          response?: {
            status?: number;
            data?: {
              message?: string;
              drift?: { stockId: number; currentQuantity: number }[];
            };
          };
        }
      )?.response;
      if (data?.status === 409 && data.data?.drift) {
        // Stock moved while counting: show the current quantities, keep the
        // counts, and let the user check those rows before saving again.
        const drift = data.data.drift;
        setRows((current) => applyDrift(current, drift));
        setDrifted(new Set(drift.map((d) => d.stockId)));
        // The user now sees the current quantities; a save after checking
        // these rows counts against them.
        const nextExpected = { ...expected };
        drift.forEach((d) => {
          if (counted[d.stockId] !== undefined) {
            nextExpected[d.stockId] = d.currentQuantity;
          }
        });
        saveDraft(counted, nextExpected);
        setMessage({
          severity: 'warning',
          text: data.data.message ?? t('stockCount.drift')
        });
      } else {
        setMessage({
          severity: 'error',
          text: data?.data?.message ?? t('stockCount.failed')
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (!branchId) {
    return (
      <Container maxWidth={false} sx={{ maxWidth: '90%', mt: 3 }}>
        <Alert severity="info">{t('stockCount.selectBranch')}</Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('stockCount.title')}</title>
      </Helmet>
      <PageTitleWrapper>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            onClick={() => navigate('/management/stock')}
            aria-label={t('back')}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h3">{t('stockCount.title')}</Typography>
            <Typography variant="subtitle2">
              {t('stockCount.subtitle', { branch: currentBranch?.name ?? '' })}
            </Typography>
          </Box>
        </Box>
      </PageTitleWrapper>

      <Container disableGutters maxWidth={false} sx={{ maxWidth: '90%' }}>
        {message && (
          <Alert
            severity={message.severity}
            sx={{ mb: 2 }}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}
        <Card sx={{ p: 2 }}>
          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            alignItems="center"
            mb={2}
          >
            <TextField
              size="small"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('stockCount.search')}
              inputProps={{ 'aria-label': t('stockCount.search') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            {warehouses.length > 1 && (
              <TextField
                select
                size="small"
                label={t('stockCount.warehouse')}
                value={warehouseId}
                onChange={(event) =>
                  setWarehouseId(
                    event.target.value === '' ? '' : Number(event.target.value)
                  )
                }
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">{t('stockCount.allWarehouses')}</MenuItem>
                {warehouses.map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={onlyCounted}
                  onChange={(event) => setOnlyCounted(event.target.checked)}
                />
              }
              label={t('stockCount.onlyCounted')}
            />
            <Box flex={1} />
            <Typography variant="body2" color="text.secondary">
              {t('stockCount.progress', {
                counted: items.length,
                total: rows.length,
                changed: changedCount
              })}
            </Typography>
          </Box>

          {loadError && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={loadRows}>
                  {t('salesReport.retry')}
                </Button>
              }
            >
              {t('stockCount.loadFailed')}
            </Alert>
          )}

          {loading && rows.length === 0 ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table
                size="small"
                stickyHeader
                aria-label={t('stockCount.title')}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{t('stockCount.col.product')}</TableCell>
                    <TableCell>{t('stockCount.col.warehouse')}</TableCell>
                    <TableCell>{t('stockCount.col.batch')}</TableCell>
                    <TableCell align="right">
                      {t('stockCount.col.system')}
                    </TableCell>
                    <TableCell align="right" sx={{ width: 180 }}>
                      {t('stockCount.col.counted')}
                    </TableCell>
                    <TableCell align="right">
                      {t('stockCount.col.difference')}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="text.secondary" align="center">
                          {t('stockCount.empty')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {visible.map((row) => {
                    const difference = countDifference(row, counted);
                    const isCounted = counted[row.id] !== undefined;
                    const unit = t(
                      prepareStockUnitLabel(row.companyProduct.stockUnit)
                    );
                    return (
                      <TableRow
                        key={row.id}
                        sx={
                          drifted.has(row.id)
                            ? {
                                background: alpha(
                                  theme.palette.warning.main,
                                  0.12
                                )
                              }
                            : undefined
                        }
                      >
                        <TableCell>{row.companyProduct.name}</TableCell>
                        <TableCell>{row.warehouse.name}</TableCell>
                        <TableCell>
                          {row.batch
                            ? `${row.batch.batchNumber}${row.batch.expiryDate ? ` · SKT ${new Date(row.batch.expiryDate).toLocaleDateString('tr-TR')}` : ''}`
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {drifted.has(row.id) ? (
                            <Tooltip title={t('stockCount.changedRow')}>
                              <span>
                                {formatQuantity(row.quantity)} {unit} ⚠
                              </span>
                            </Tooltip>
                          ) : (
                            `${formatQuantity(row.quantity)} ${unit}`
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <NumericInput
                            size="small"
                            value={counted[row.id] ?? 0}
                            showEmptyForZero={!isCounted}
                            allowDecimals={
                              row.companyProduct.stockUnit !== StockUnit.PIECE
                            }
                            onChange={(value) => setRowCount(row, value)}
                            onEmpty={() => clearRowCount(row.id)}
                            inputProps={{
                              'aria-label': t('stockCount.countedFor', {
                                product: row.companyProduct.name
                              })
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 600,
                            color:
                              difference === null || difference === 0
                                ? 'text.secondary'
                                : difference > 0
                                  ? 'success.main'
                                  : 'error.main'
                          }}
                        >
                          {difference === null
                            ? '—'
                            : `${difference > 0 ? '+' : ''}${formatQuantity(difference)}`}
                        </TableCell>
                        <TableCell padding="checkbox">
                          {isCounted && (
                            <Tooltip title={t('stockCount.clearRow')}>
                              <IconButton
                                size="small"
                                aria-label={t('stockCount.clearRow')}
                                onClick={() => clearRowCount(row.id)}
                              >
                                <UndoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            alignItems="center"
            mt={2}
          >
            <TextField
              size="small"
              value={note}
              onChange={(event) => setNote(event.target.value.slice(0, 200))}
              label={t('stockCount.note')}
              sx={{ minWidth: 280, flex: 1 }}
            />
            <Button
              variant="text"
              disabled={items.length === 0 || saving}
              onClick={() => saveDraft({}, {})}
            >
              {t('stockCount.clearAll')}
            </Button>
            <Button
              variant="contained"
              disabled={items.length === 0 || saving}
              onClick={() => setConfirmOpen(true)}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}
            >
              {t('stockCount.save', { n: items.length })}
            </Button>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={1}
            sx={{ textTransform: 'none' }}
          >
            {t('stockCount.hint')}
          </Typography>
        </Card>
      </Container>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleSave}
        title={t('stockCount.confirm.title')}
        message={t('stockCount.confirm.message', {
          counted: items.length,
          changed: changedCount
        })}
        confirmButtonText={t('stockCount.confirm.button')}
      />
    </>
  );
}

export default StockCountPage;
